/* ===========================================================================
   Akaro custom behaviour
   ---------------------------------------------------------------------------
   No framework, no build step. One small module per custom section.

   Every module is initialised on DOMContentLoaded and re-initialised on
   `shopify:section:load` so sections behave correctly when a merchant adds or
   edits them in the theme editor.

   Motion policy: reduced motion is the default. Modules must check
   `prefersReducedMotion()` and degrade to a static state — never just run the
   animation faster.
   =========================================================================== */

(function () {
  'use strict';

  var INIT_FLAG = 'akaroInit';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* -------------------------------------------------------------------------
     Hero video

     The <video> is rendered with preload="none" and no autoplay attribute, so
     nothing is fetched until we explicitly ask. Reduced-motion visitors never
     trigger that, meaning they save the download as well as the movement.
     ------------------------------------------------------------------------- */
  function initHero(root) {
    var video = root.querySelector('[data-akaro-hero-video] video');
    if (!video) return;

    if (prefersReducedMotion()) return;

    // Required for inline autoplay on iOS; belt and braces alongside video_tag.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.loop = true;
    video.preload = 'auto';

    video.addEventListener(
      'playing',
      function () {
        root.setAttribute('data-video-playing', 'true');
      },
      { once: true }
    );

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Autoplay refusal is not an error worth surfacing — the poster image is
      // a complete experience on its own.
      attempt.catch(function () {});
    }
  }

  /* -------------------------------------------------------------------------
     Remote demo — light modes + dimmer

     Modes are a real radio group in the markup, so keyboard and screen-reader
     behaviour comes from the platform rather than from JS. This only reflects
     the current selection onto the flame element.
     ------------------------------------------------------------------------- */
  function initRemoteDemo(root) {
    var stage = root.querySelector('[data-akaro-flame-stage]');
    if (!stage) return;

    var radios = root.querySelectorAll('[data-akaro-mode]');
    var dimmer = root.querySelector('[data-akaro-dimmer]');
    var dimmerOut = root.querySelector('[data-akaro-dimmer-output]');

    function applyMode(value) {
      stage.setAttribute('data-mode', value);
    }

    Array.prototype.forEach.call(radios, function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) applyMode(radio.value);
      });
      if (radio.checked) applyMode(radio.value);
    });

    if (dimmer) {
      // Five discrete levels, matching the physical remote (the packaging
      // states "5 Dimstanden"). Spacing is perceptual rather than linear —
      // equal steps in brightness value would look bunched at the top,
      // because perceived brightness is not proportional to luminance.
      var DIM_LEVELS = [0.22, 0.4, 0.62, 0.81, 1];

      var syncDimmer = function () {
        var level = parseInt(dimmer.value, 10);
        if (isNaN(level)) level = 3;
        level = Math.max(1, Math.min(5, level));

        stage.style.setProperty('--akaro-dim', String(DIM_LEVELS[level - 1]));

        // Range inputs announce their raw number; without this a screen reader
        // says "3" with no unit or scale.
        dimmer.setAttribute('aria-valuetext', 'Stand ' + level + ' van 5');
        if (dimmerOut) dimmerOut.textContent = level + ' / 5';
      };

      dimmer.addEventListener('input', syncDimmer);
      syncDimmer();
    }
  }

  /* -------------------------------------------------------------------------
     Product page — gallery, quantity stepper, ajax cart

     The add-to-cart form is a complete no-JS experience (a normal POST to
     /cart/add); everything here is enhancement. Cart behaviour is delegated
     to Impulse's own theme.AjaxProduct so the cart drawer, loading state and
     events stay consistent with the rest of the theme without editing any
     vendor file.
     ------------------------------------------------------------------------- */
  function initProduct(root) {
    // Gallery: thumbnails swap the active image. On mobile the stage is a
    // scroll strip and the thumbs are hidden, so this only drives desktop.
    var thumbs = root.querySelectorAll('[data-akaro-thumb]');
    var items = root.querySelectorAll('[data-akaro-media-item]');

    // A video plays only while its slide is active: switching away pauses it,
    // switching to it starts it muted. Under reduced motion nothing autoplays —
    // the visitor uses the native controls.
    function syncVideos(activeIndex) {
      Array.prototype.forEach.call(items, function (item, i) {
        var video = item.querySelector('video');
        if (!video) return;
        if (i === activeIndex && !prefersReducedMotion()) {
          video.muted = true;
          var attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(function () {});
          }
        } else {
          video.pause();
        }
      });
    }

    Array.prototype.forEach.call(thumbs, function (thumb, index) {
      thumb.addEventListener('click', function () {
        Array.prototype.forEach.call(items, function (item, i) {
          if (i === index) {
            item.setAttribute('data-active', '');
          } else {
            item.removeAttribute('data-active');
          }
        });
        Array.prototype.forEach.call(thumbs, function (t, i) {
          if (i === index) {
            t.setAttribute('aria-current', 'true');
          } else {
            t.removeAttribute('aria-current');
          }
        });
        syncVideos(index);
      });
    });

    // Quantity stepper. The input stays a real number field, so typing and
    // form submission work without this; the buttons are the big-tap-target
    // path for the audience that finds number inputs fiddly.
    var qtyInput = root.querySelector('[data-akaro-qty-input]');
    var qtyMinus = root.querySelector('[data-akaro-qty-minus]');
    var qtyPlus = root.querySelector('[data-akaro-qty-plus]');

    function stepQty(delta) {
      if (!qtyInput) return;
      var value = parseInt(qtyInput.value, 10);
      if (isNaN(value)) value = 1;
      value = Math.max(1, value + delta);
      qtyInput.value = String(value);
    }

    if (qtyMinus) qtyMinus.addEventListener('click', function () { stepQty(-1); });
    if (qtyPlus) qtyPlus.addEventListener('click', function () { stepQty(1); });

    // Ajax cart via the vendor's own machinery. Its default submit selector
    // is '.add-to-cart', which our button carries.
    var form = root.querySelector('[data-akaro-product-form]');
    if (form && window.theme && typeof theme.AjaxProduct === 'function') {
      new theme.AjaxProduct(form);
    }
  }

  /* -------------------------------------------------------------------------
     Store locator — zoekfilter over lijst + kaartstippen

     De volledige lijst en de kaart staan al in de HTML; dit filtert alleen.
     Zonder JS is de pagina compleet. Matching is diakriet- en spatie-
     ongevoelig, zodat "Fryslan", "bergen op zoom" en "6602AH" gewoon werken.
     ------------------------------------------------------------------------- */
  function initStoreLocator(root) {
    initLocatorMap(root);

    var input = root.querySelector('[data-akaro-locator-input]');
    if (!input) return;

    var stores = root.querySelectorAll('[data-akaro-store]');
    var dots = root.querySelectorAll('[data-akaro-dot]');
    var groups = root.querySelectorAll('[data-akaro-locator-group]');
    var empty = root.querySelector('[data-akaro-locator-empty]');
    var count = root.querySelector('[data-akaro-locator-count]');

    var msgCount = root.getAttribute('data-msg-count') || '%d winkels gevonden';
    var msgCountOne = root.getAttribute('data-msg-count-one') || '1 winkel gevonden';

    function normalise(value) {
      value = value.toLowerCase();
      // NFD splitst letters en accenten ("Fryslân" wordt "Frysla" + accent).
      if (typeof value.normalize === 'function') {
        value = value.normalize('NFD');
      }
      // Alles behalve letters en cijfers weg: losse accenten, spaties en
      // leestekens tegelijk — zodat "6602AH" en "bergen op zoom" matchen.
      return value.replace(/[^a-z0-9]/g, '');
    }

    function applyFilter() {
      var query = normalise(input.value);
      var visibleHandles = {};
      var visibleCount = 0;

      Array.prototype.forEach.call(stores, function (store) {
        var match = query === '' || normalise(store.getAttribute('data-search') || '').indexOf(query) !== -1;
        if (match) {
          store.removeAttribute('hidden');
          visibleHandles[store.getAttribute('data-handle')] = true;
          visibleCount++;
        } else {
          store.setAttribute('hidden', '');
        }
      });

      Array.prototype.forEach.call(groups, function (group) {
        var any = group.querySelector('[data-akaro-store]:not([hidden])');
        if (any) {
          group.removeAttribute('hidden');
        } else {
          group.setAttribute('hidden', '');
        }
      });

      // Stippen vervagen in plaats van verdwijnen — de kaart blijft leesbaar.
      Array.prototype.forEach.call(dots, function (dot) {
        if (query === '' || visibleHandles[dot.getAttribute('data-handle')]) {
          dot.classList.remove('is-dim');
        } else {
          dot.classList.add('is-dim');
        }
      });

      if (empty) {
        if (visibleCount === 0) {
          empty.removeAttribute('hidden');
        } else {
          empty.setAttribute('hidden', '');
        }
      }

      if (count) {
        if (query === '') {
          count.textContent = '';
        } else if (visibleCount === 1) {
          count.textContent = msgCountOne;
        } else {
          count.textContent = msgCount.replace('%d', String(visibleCount));
        }
      }
    }

    input.addEventListener('input', applyFilter);
    // type="search" heeft een eigen wisknopje; dat vuurt 'search'.
    input.addEventListener('search', applyFilter);
  }

  /* -------------------------------------------------------------------------
     Store locator — kaart: zoomen, pannen en "toon mijn locatie"

     De viewBox is de enige bron van waarheid: zoomen verkleint hem, pannen
     verschuift hem. Pannen kan alleen ingezoomd, zodat de kaart op mobiel
     nooit het scrollen van de pagina kaapt. De locatievraag start pas na een
     bewuste klik op de knop (de browser toont dan zelf de toestemmingsvraag);
     de positie blijft in de browser en verlaat de pagina niet.
     ------------------------------------------------------------------------- */
  function initLocatorMap(root) {
    var svg = root.querySelector('[data-akaro-map]');
    if (!svg) return;

    var BASE_W = 600;
    var BASE_H = 711;
    var MAX_SCALE = 4;
    var scale = 1;
    var cx = BASE_W / 2;
    var cy = BASE_H / 2;
    var marker = null;
    var markerX = 0;
    var markerY = 0;

    function apply() {
      var w = BASE_W / scale;
      var h = BASE_H / scale;
      cx = Math.min(Math.max(cx, w / 2), BASE_W - w / 2);
      cy = Math.min(Math.max(cy, h / 2), BASE_H - h / 2);
      svg.setAttribute('viewBox', (cx - w / 2) + ' ' + (cy - h / 2) + ' ' + w + ' ' + h);
      svg.classList.toggle('is-zoomed', scale > 1);
      if (marker) {
        // Tegenschalen zodat de markering even groot blijft op elk zoomniveau.
        marker.setAttribute('transform', 'translate(' + markerX + ' ' + markerY + ') scale(' + (1 / scale) + ')');
      }
      if (zoomIn) zoomIn.disabled = scale >= MAX_SCALE - 0.01;
      if (zoomOut) zoomOut.disabled = scale <= 1.01;
    }

    var controls = root.querySelector('[data-akaro-map-controls]');
    var zoomIn = root.querySelector('[data-akaro-zoom-in]');
    var zoomOut = root.querySelector('[data-akaro-zoom-out]');

    if (controls) controls.hidden = false;

    if (zoomIn) {
      zoomIn.addEventListener('click', function () {
        scale = Math.min(MAX_SCALE, scale * 1.5);
        apply();
      });
    }

    if (zoomOut) {
      zoomOut.addEventListener('click', function () {
        scale = scale / 1.5;
        if (scale < 1.05) {
          scale = 1;
          cx = BASE_W / 2;
          cy = BASE_H / 2;
        }
        apply();
      });
    }

    // Pannen met muis of vinger, alleen ingezoomd. Na echt slepen wordt de
    // eerstvolgende klik onderdrukt, zodat je niet per ongeluk op een
    // winkelstip "klikt" waar het slepen toevallig begon.
    var panning = false;
    var moved = false;
    var lastX = 0;
    var lastY = 0;

    svg.addEventListener('pointerdown', function (event) {
      if (scale <= 1) return;
      panning = true;
      moved = false;
      lastX = event.clientX;
      lastY = event.clientY;
      svg.classList.add('is-panning');
      if (svg.setPointerCapture) svg.setPointerCapture(event.pointerId);
    });

    svg.addEventListener('pointermove', function (event) {
      if (!panning) return;
      var dx = event.clientX - lastX;
      var dy = event.clientY - lastY;
      if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;
      lastX = event.clientX;
      lastY = event.clientY;
      var unitsPerPixel = (BASE_W / scale) / svg.getBoundingClientRect().width;
      cx -= dx * unitsPerPixel;
      cy -= dy * unitsPerPixel;
      apply();
    });

    function endPan() {
      panning = false;
      svg.classList.remove('is-panning');
    }

    svg.addEventListener('pointerup', endPan);
    svg.addEventListener('pointercancel', endPan);

    svg.addEventListener('click', function (event) {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);

    // "Toon mijn locatie" — alleen aanbieden als de browser het kan.
    var locate = root.querySelector('[data-akaro-locate]');
    var status = root.querySelector('[data-akaro-locate-status]');

    if (locate && navigator.geolocation) {
      locate.hidden = false;

      var lng0 = parseFloat(svg.getAttribute('data-proj-lng0'));
      var kx = parseFloat(svg.getAttribute('data-proj-kx'));
      var lat0 = parseFloat(svg.getAttribute('data-proj-lat0'));
      var ky = parseFloat(svg.getAttribute('data-proj-ky'));

      function say(message) {
        if (status) status.textContent = message;
      }

      locate.addEventListener('click', function () {
        locate.disabled = true;
        say('');
        navigator.geolocation.getCurrentPosition(
          function (position) {
            locate.disabled = false;
            var x = (position.coords.longitude - lng0) * kx;
            var y = (lat0 - position.coords.latitude) * ky;
            if (x < 0 || x > BASE_W || y < 0 || y > BASE_H) {
              say(locate.getAttribute('data-msg-outside') || '');
              return;
            }
            if (!marker) {
              var ns = 'http://www.w3.org/2000/svg';
              marker = document.createElementNS(ns, 'g');
              marker.setAttribute('aria-hidden', 'true');
              var halo = document.createElementNS(ns, 'circle');
              halo.setAttribute('class', 'akaro-locator__you-halo');
              halo.setAttribute('cx', '0');
              halo.setAttribute('cy', '-20');
              halo.setAttribute('r', '17');
              var pin = document.createElementNS(ns, 'path');
              pin.setAttribute('class', 'akaro-locator__you-pin');
              // Pijlvormige speld: de punt (0,0) staat exact op de locatie.
              pin.setAttribute('d', 'M0 0 C -6 -9 -11 -13 -11 -20 A 11 11 0 1 1 11 -20 C 11 -13 6 -9 0 0 Z');
              var dot = document.createElementNS(ns, 'circle');
              dot.setAttribute('class', 'akaro-locator__you-halo');
              dot.setAttribute('cx', '0');
              dot.setAttribute('cy', '-20');
              dot.setAttribute('r', '4');
              dot.style.opacity = '1';
              marker.appendChild(halo);
              marker.appendChild(pin);
              marker.appendChild(dot);
              svg.appendChild(marker);
            }
            markerX = x;
            markerY = y;
            cx = x;
            cy = y;
            apply();
            say(locate.getAttribute('data-msg-found') || '');
          },
          function () {
            locate.disabled = false;
            say(locate.getAttribute('data-msg-error') || '');
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      });
    }

    apply();
  }

  /* -------------------------------------------------------------------------
     Social wall — reviewstrip

     Handmatig bladeren, niets beweegt vanzelf (Tom, 2026-09-26). Mobiel swipe
     je; vanaf 750px staan er pijlen links en rechts van de kaarten. Eén klik
     schuift precies twee reviews op (Tom, 2026-09-26).

     Oneindige cyclus: de kaarten worden één keer gekloond, zodat achter de
     laatste review de eerste weer volgt. De klonen zijn aria-hidden, dus een
     schermlezer hoort elke review maar één keer.

     De truc voor een naadloze wrap is dat de sprong VÓÓR de animatie gebeurt:
     staat het doel voorbij één volledige set, dan verspringt scrollLeft eerst
     een setbreedte terug (inhoud is identiek, dus onzichtbaar) en pas daarna
     wordt er smooth naartoe gescrold. Andersom net zo. Springen tijdens een
     lopende smooth-animatie zou juist wél zichtbaar zijn.

     Omdat de strip rondloopt worden de knoppen nooit disabled — er is geen
     begin en geen einde meer.
     ------------------------------------------------------------------------- */
  function initSocialWall(root) {
    var wall = root.querySelector('[data-akaro-social-wall]');
    if (!wall) return;

    var prev = root.querySelector('[data-akaro-social-prev]');
    var next = root.querySelector('[data-akaro-social-next]');
    var cards = wall.querySelectorAll('.akaro-social__card');

    // De strip is met het toetsenbord te scrollen zodra hij echt scrolt.
    if (wall.scrollWidth > wall.clientWidth + 4) wall.setAttribute('tabindex', '0');

    if (!prev || !next || cards.length < 2) return;
    if (wall.scrollWidth <= wall.clientWidth + 4) return; // past allemaal in beeld

    var clones = [];
    Array.prototype.forEach.call(cards, function (card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-akaro-clone', '');
      clone.removeAttribute('id');
      wall.appendChild(clone);
      clones.push(clone);
    });

    prev.hidden = false;
    next.hidden = false;

    // Breedte van één volledige set = afstand tussen origineel 1 en kloon 1.
    function setBreedte() {
      return clones[0].offsetLeft - cards[0].offsetLeft;
    }

    // Eén stap = twee kaarten, inclusief de tussenruimte.
    function stap() {
      var breedte = cards[1].offsetLeft - cards[0].offsetLeft;
      if (breedte <= 0) breedte = cards[0].offsetWidth;
      return breedte * 2;
    }

    function schuif(richting) {
      var loop = setBreedte();
      var doel = wall.scrollLeft + richting * stap();

      if (loop > 0) {
        if (doel >= loop) {
          wall.scrollLeft -= loop;
          doel -= loop;
        } else if (doel < 0) {
          wall.scrollLeft += loop;
          doel += loop;
        }
      }

      wall.scrollTo({
        left: doel,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    // Ook met de hand of met het toetsenbord blijft de cirkel een cirkel.
    wall.addEventListener('scroll', function () {
      var loop = setBreedte();
      if (loop > 0 && wall.scrollLeft >= loop) wall.scrollLeft -= loop;
    }, { passive: true });

    prev.addEventListener('click', function () { schuif(-1); });
    next.addEventListener('click', function () { schuif(1); });
  }

  /* ---------------------------------------------------------------------- */

  var modules = [
    { selector: '[data-akaro-hero]', fn: initHero },
    { selector: '[data-akaro-remote-demo]', fn: initRemoteDemo },
    { selector: '[data-akaro-pdp]', fn: initProduct },
    { selector: '[data-akaro-store-locator]', fn: initStoreLocator },
    { selector: '[data-akaro-social]', fn: initSocialWall }
  ];

  function init(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    modules.forEach(function (mod) {
      var nodes = scope.querySelectorAll(mod.selector);

      // The scope itself can be the match when re-initialising a single section.
      if (scope.matches && scope.matches(mod.selector)) {
        nodes = [scope];
      }

      Array.prototype.forEach.call(nodes, function (node) {
        if (node.dataset[INIT_FLAG] === 'true') return;
        node.dataset[INIT_FLAG] = 'true';
        try {
          mod.fn(node);
        } catch (err) {
          // One broken section must never take the rest of the page down.
          if (window.console && console.warn) {
            console.warn('[akaro] section init failed:', mod.selector, err);
          }
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
